/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Config domain - Game constants and configuration management
 * config.common.ts - All game configuration constants and parameters
 */

import { createConfig } from "./registry";
import { MATH } from "./utils/constants";

// ============================================================================
// WORLD & LOBBY
// ============================================================================
export const WORLD = { w: 5000, h: 5000 }; // Medium-sized world
export const MAX_PER_LOBBY = 100;
export const LOBBY_COUNTDOWN = 3000; // 3 seconds until game starts (reduced for testing)
export const MIN_PLAYERS = 1; // Minimum players to keep countdown going (reduced for testing)
export const MAX_NAME = 16;

// ============================================================================
// TEAM MODES & CTF CONFIGURATION
// ============================================================================
export const SPAWN_RADIUS = 500; // Distance within which players spawn near their team's base (10x capture radius)
export const FLAG_PICKUP_RADIUS = 30; // Distance within which a player can pick up a flag
export const FLAG_CAPTURE_RADIUS = 50; // Distance within which a player can capture at base
export const CTF_FLAG_CARRIER_SPEED_MULTIPLIER = 0.9; // Speed penalty when carrying a flag

// ============================================================================
// GAME LOOP & NETWORKING
// ============================================================================
const isE2EMode =
  typeof process !== "undefined" && process.env?.E2E_MODE === "true";
export const TICK_HZ = isE2EMode ? 60 : 60; // Server physics tick rate
export const BROADCAST_HZ = isE2EMode ? 60 : 90; // Network broadcast rate - higher = smoother with latency
export const TICK_MS = Math.floor(1000 / TICK_HZ);
export const BROADCAST_MS = Math.floor(1000 / BROADCAST_HZ);

// ============================================================================
// PLAYER CONFIGURATION
// ============================================================================
export const PLAYER_RADIUS = 10;
export const MAX_HEALTH = 25;
export const ELIMINATION_DELAY = 3; // seconds - time before kicking dead players
export const PLAYER_COLLISION_DAMAGE = 5; // Damage when players collide with each other
export const COLLISION_COOLDOWN_MS = 200; // Milliseconds before same entities can collide again

// ============================================================================
// PHYSICS CONFIGURATION
// ============================================================================
export const FRICTION = 0.98; // Reduced friction for more responsive movement
export const MAX_ANGULAR_VELOCITY = MATH.DEG_TO_RAD * 300; // 300°/s - faster turning
export const TARGET_ROTATION_RATE = MATH.DEG_TO_RAD * 180; // 180°/s - snappier response
export const ROTATION_SNAP_THRESHOLD = MATH.DEG_TO_RAD * 15; // 15° snap - wider stability zone
// Mouse-specific rotation constants
export const MIN_MOUSE_DISTANCE = 15; // Pixels - minimum mouse distance from center to register rotation
export const MOUSE_ROTATION_SPEED = MATH.DEG_TO_RAD * 1000; // 1000°/s - very fast servo for mouse
export const THRUST = 450; // Forward thrust force - increased for more responsive acceleration
export const MAX_SPEED = 600; // Maximum velocity in px/s

// Per-tick physics constants (derived from per-second values)
// These match the server's inline constants in apps/stdb/src/index.ts
export const PLAYER_THRUST_PER_TICK = THRUST / TICK_HZ; // 450/60 = 7.5
export const PLAYER_BASE_SPEED_PER_TICK = MAX_SPEED / TICK_HZ; // 600/60 = 10
export const PLAYER_BOOST_MULTIPLIER = 1.6;
export const STRAFE_THRUST_MULTIPLIER = 0.7;
export const PLAYER_ROTATION_STEP = 0.13;
export const MOUSE_MAX_ROTATION_PER_TICK = MOUSE_ROTATION_SPEED / TICK_HZ;
export const ROTATION_SNAP_THRESHOLD_TICKS = (Math.PI / 180) * 15;

// ============================================================================
// COMBAT CONFIGURATION
// ============================================================================
export const BULLET_SPEED = 500; // px/s - Reduced for smoother visuals
export const BULLET_LIFETIME = 3.0; // seconds
export const SHOOT_COOLDOWN = 1.0; // seconds
export const BULLET_RADIUS = 5.0;
export const BULLET_BASE_DAMAGE = 1.0; // Base damage for all bullets (scales with player stats)

// Mine configuration
export const MINE_RADIUS = 6; // Visual radius for mine entity
export const MINE_BASE_HEALTH = 15; // Base health for mines (scales with pellets) - increased to prevent one-shots
export const MINE_TIMER = 4.0; // seconds before automatic explosion
export const MINE_COOLDOWN = 8.0; // seconds between deployments
export const MINE_EXPLOSION_RADIUS = 80; // pixels - AOE damage radius
export const MINE_EXPLOSION_DAMAGE = 50; // Universal damage to all entities
export const MINE_FRICTION = 0.98; // Friction applied to mine velocity

// ============================================================================
// ASTEROID CONFIGURATION
// ============================================================================
export const INITIAL_ASTEROID_COUNT = 30; // Total number of asteroids spawned at game start (all sizes)
export const ASTEROID_COLLISION_DAMAGE = 20; // Damage when player hits asteroid
export const ASTEROID_SIZES = [150, 75, 50, 25, 15]; // Radius for each size level (0=largest, 4=smallest)
export const ASTEROID_BREAKUP_COUNTS = [3, 2, 2, 1, 0]; // How many pieces each size breaks into
export const ASTEROID_MIN_SPEED = 20; // Minimum drift speed for asteroids
export const ASTEROID_MAX_SPEED = 100; // Maximum drift speed for asteroids
export const ASTEROID_MAX_HEALTH = [50, 25, 20, 15, 10]; // Health for each size level (0=largest, 4=smallest)

// ============================================================================
// PLAYER SCALING CONFIGURATION - Tweak these for game balance!
// ============================================================================
export const DAMAGE_SCALING_RATE = 0.005; // damage_multiplier = 1 + pellets^1.3 × rate (accelerating growth, balanced top-end)
export const SIZE_SCALING_RATE = 0.05; // scale = 1 + sqrt(pellets) × rate
export const SPEED_SCALING_RATE = 0.2; // speed_multiplier = 1 + ln(pellets + 1) × rate
export const MAGNET_SCALING_RATE = 10; // magnetRange = base + sqrt(pellets) × rate

// ============================================================================
// MINE DAMAGE & HEALTH SCALING CONFIGURATION - Easy tweaking for balance
// ============================================================================
export const MINE_DAMAGE_SCALING_RATE = 1.0; // Linear scaling multiplier
export const MINE_HEALTH_SCALING_RATE = 0.1; // Health scaling multiplier

// Target mine damage scaling: 2x player health at each pellet level
// 0 pellets: 25hp → 50 damage (2x)
// 100 pellets: 125hp → 150 damage (2x)
// 1000 pellets: 1025hp → 1050 damage (2x)
// Formula: MINE_EXPLOSION_DAMAGE + pellets × MINE_DAMAGE_SCALING_RATE

// Target mine health scaling: More resilient mines for advanced players
// 0 pellets: 15hp (base) - prevents one-shotting
// 100 pellets: 25hp (1.7x base)
// 1000 pellets: 115hp (7.7x base)
// Formula: MINE_BASE_HEALTH + pellets × MINE_HEALTH_SCALING_RATE

/**
 * Calculate mine explosion damage based on owner's pellets collected
 * Centralized calculation for easy tweaking and testing
 */
export function calculateMineExplosionDamage(pelletsCollected: number): number {
  return Math.ceil(
    MINE_EXPLOSION_DAMAGE + pelletsCollected * MINE_DAMAGE_SCALING_RATE,
  );
}

/**
 * Calculate mine health based on owner's pellets collected
 * Centralized calculation for easy tweaking and testing
 */
export function calculateMineHealth(pelletsCollected: number): number {
  return Math.ceil(
    MINE_BASE_HEALTH + pelletsCollected * MINE_HEALTH_SCALING_RATE,
  );
}

// ============================================================================
// SPEED-BASED COMBAT SCALING - Balance control knobs
// ============================================================================
export const SPEED_AFFECTS_ATTACK_RATE = true; // Enable/disable attack rate scaling
export const SPEED_AFFECTS_BULLET_VELOCITY = true; // Enable/disable bullet speed scaling
export const SPEED_ATTACK_RATE_SCALING: number = 1.0; // Multiplier for attack rate scaling (1.0 = full scaling)
export const SPEED_BULLET_VELOCITY_SCALING: number = 1.0; // Multiplier for bullet speed scaling (1.0 = full scaling)
export const SPEED_AFFECTS_BULLET_LIFETIME = true; // Enable/disable bullet lifetime scaling (shorter lifetime for faster bullets)
export const SPEED_BULLET_LIFETIME_SCALING: number = 1.0; // Multiplier for bullet lifetime scaling (1.0 = full inverse scaling)
export const MAX_SPEED_MULTIPLIER_CAP = 5.0; // Cap to prevent extreme values

// SPEED_ATTACK_RATE_SCALING examples:
// 0.0 = No attack rate scaling (original behavior)
// 0.5 = Half effect (2x speed = 1.5x attack rate)
// 1.0 = Full effect (2x speed = 2x attack rate)
// 2.0 = Double effect (2x speed = 3x attack rate)

// SPEED_BULLET_VELOCITY_SCALING examples:
// 0.0 = No bullet speed scaling (500 px/s always)
// 1.0 = Full scaling (2x speed = 1000 px/s bullets)
// 0.5 = Half scaling (2x speed = 750 px/s bullets)

// SPEED_BULLET_LIFETIME_SCALING examples:
// 0.0 = No bullet lifetime scaling (3.0s always)
// 1.0 = Full inverse scaling (2x speed = 1.5s lifetime)
// 0.5 = Half inverse scaling (2x speed = 2.25s lifetime)

// ============================================================================
// PELLET SYSTEM CONFIGURATION
// ============================================================================
export const PELLET_SPAWN_COUNT_MIN = [50, 30, 20, 10, 6]; // Minimum pellets spawned per asteroid destruction by size level (0=largest, 4=smallest) - 2x original
export const PELLET_SPAWN_COUNT_MAX = [100, 50, 30, 20, 10]; // Maximum pellets spawned per asteroid destruction by size level (0=largest, 4=smallest) - 2x original
export const PELLET_SPAWN_RADIUS = 20; // Maximum distance pellets spawn from asteroid center (pixels)
export const PELLET_RADIUS = 8; // Collision radius for pellets
export const PELLET_LIFETIME = 10; // Pellet despawn time in seconds
export const PELLET_MAX_WORLD_COUNT = 10000; // Maximum pellets allowed in world simultaneously
export const PELLET_MAGNETIC_MAX_SPEED = 400; // Maximum magnetic pull velocity
export const PELLET_MAGNETIC_INTERACTIONS_PER_FRAME = 50; // Max magnetic interactions per player per frame
export const PELLET_BASE_MAGNETIC_RADIUS = 100; // Base radius for magnetic attraction (pixels)
export const PELLET_HEALTH_GROWTH_SCALE = 0.5; // Multiplier for health gained per pellet value (0.5 = half a health point per pellet)

// Pellet death drop immunity system
export const PELLET_DEATH_IMMUNITY_MS = 500; // 0.5 second immunity after player death drops
export const PELLET_DEATH_SCATTER_SPEED_MIN = 300; // Minimum scatter speed for death drops
export const PELLET_DEATH_SCATTER_SPEED_MAX = 800; // Maximum scatter speed for death drops

// Pellet rewards from asteroid hits (not destruction)
export const ASTEROID_HIT_PELLET_COUNT_MIN = 0;
export const ASTEROID_HIT_PELLET_COUNT_MAX = 2;
export const ASTEROID_HIT_PELLET_SPAWN_DISTANCE_MIN = 5;
export const ASTEROID_HIT_PELLET_SPAWN_DISTANCE_MAX = 15;
export const ASTEROID_HIT_PELLET_VELOCITY_MIN = 30;
export const ASTEROID_HIT_PELLET_VELOCITY_MAX = 70;

// ============================================================================
// LOGGING CONFIGURATION
// ============================================================================
export const LOG_LEVEL_DEFAULT = "info";
export const LOG_LEVEL_CLIENT_DEFAULT = "debug"; // More verbose in browser for development

export const LOG_SENSITIVE_FIELDS = [
  "password",
  "token",
  "secret",
  "key",
  "auth",
  "cookie",
  "session",
];
export const LOG_REDACTED_TEXT = "[REDACTED]";

export const LOG_LEVELS = {
  silent: 70,
  fatal: 60,
  error: 50,
  warn: 40,
  test: 35,
  info: 30,
  debug: 20,
  trace: 10,
} as const;

export const LOG_CATEGORIES = [
  "QUEUE",
  "LOBBY_MGR",
  "DATABASE",
  "PLAYER",
  "BOT",
  "CLIENT",
  "APP",
  "UI",
  "GAME",
  "WEBSOCKET",
  "SERVER",
] as const;

// Module-specific log level configuration following PRD specification
export const MODULE_LOG_LEVELS = {
  development: {
    physics: "debug",
    lobby: "info",
    networking: "trace",
    bot: "debug",
    game: "info",
    collision: "debug",
    player: "info",
    rendering: "debug",
    input: "debug",
    ui: "info",
    audio: "info",
    default: "info",
  },
  production: {
    default: "info",
  },
} as const;

// Globally disabled log categories - these will always be silenced
// To re-enable any category, simply remove it from this array
// To disable additional categories, add them here
export const DISABLED_LOG_CATEGORIES = [
  "QUEUE", // Queue management logs
  "LOBBY_MGR", // Lobby manager logs
  "LOBBY_CLIENT", // Client-side lobby logs
  "CLIENT", // Client logs (damage, particles, etc.)
] as const;

/**
 * Get log level for a specific module based on environment
 */
export function getModuleLogLevel(moduleName: string): string {
  const env =
    typeof process !== "undefined" ? process?.env?.NODE_ENV : "development";
  const envConfig =
    env === "production"
      ? MODULE_LOG_LEVELS.production
      : MODULE_LOG_LEVELS.development;

  // Check for specific module level first, then fall back to default
  return envConfig[moduleName as keyof typeof envConfig] || envConfig.default;
}

// ============================================================================
// DAMAGE NUMBERS CONFIGURATION
// ============================================================================
export const DAMAGE_NUMBER_MIN_FONT_SIZE = 12; // Minimum font size for damage numbers (damage 1)
export const DAMAGE_NUMBER_MAX_FONT_SIZE = 48; // Maximum font size for damage numbers (damage 256+)
export const DAMAGE_NUMBER_MAX_DAMAGE_FOR_SCALING = 256; // Damage value that reaches max font size
export const DAMAGE_NUMBER_MIN_MOVEMENT = 25; // Minimum upward movement in pixels (damage 1)
export const DAMAGE_NUMBER_MAX_MOVEMENT = 60; // Maximum upward movement in pixels (damage 256+)

// ============================================================================
// UI & RENDERING
// ============================================================================

// Font configuration - centralized font family for all UI elements
export const FONT_FAMILY = "Orbitron"; // Main UI font
export const FONT_FAMILY_MONOSPACE = "JetBrains Mono"; // ASCII art and monospace elements
export const FONT_FAMILY_CSS = `'${FONT_FAMILY}', monospace`; // CSS font stack
export const FONT_FAMILY_CANVAS = `${FONT_FAMILY}, monospace`; // Canvas font stack
export const COLORS = {
  PLAYER: "#4ade80",
  ENEMY: "#ef4444",
  PLAYER_RGB: "74, 222, 128",
  ENEMY_RGB: "239, 68, 68",
  BLUE: "#3b82f6",
  RED: "#ef4444",
} as const;

// Available player color palette (from UI color picker)
export const PLAYER_COLOR_PALETTE = [
  "#4ade80", // default (green)
  "#ef4444", // red
  "#3b82f6", // blue
  "#8b5cf6", // purple
  "#f97316", // orange
  "#06b6d4", // cyan
];

// Color name to hex mapping (matches UI data-color attributes)
export const COLOR_NAME_TO_HEX: Record<string, string> = {
  default: "#4ade80", // green
  red: "#ef4444",
  blue: "#3b82f6",
  purple: "#8b5cf6",
  orange: "#f97316",
  cyan: "#06b6d4",
};

// Function to convert color name to hex value
export function getColorHex(colorName: string): string {
  return COLOR_NAME_TO_HEX[colorName] || COLOR_NAME_TO_HEX.default || "#00FF00";
}

// ============================================================================
// TEAM CONFIGURATION
// ============================================================================
export const TEAM_COLORS = {
  0: COLORS.BLUE,
  1: COLORS.RED,
} as const;

export const TEAM_NAMES = {
  0: "Team Blue",
  1: "Team Red",
} as const;

// Helper function to get team name by team ID
export function getTeamName(teamId: number): string {
  return TEAM_NAMES[teamId as keyof typeof TEAM_NAMES] || `Team ${teamId}`;
}

// Helper function to get team color by team ID
export function getTeamColor(teamId: number): string {
  return TEAM_COLORS[teamId as keyof typeof TEAM_COLORS] || "#808080";
}

// ============================================================================
// VERSION & BUILD INFORMATION
// ============================================================================
// Version and build timestamp are generated at BUILD TIME by scripts/generate-build-info.ts
// This ensures the same version/timestamp across all modules in a build
export { APP_VERSION, BUILD_TIMESTAMP } from "./build-info.generated";

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
export const DEBUG_PELLET_BOOST_AMOUNT = 1000; // Number of pellets given by debug combo (Ctrl+Shift+P)

// ============================================================================
// BOT SYSTEM CONFIGURATION (FR-001 through FR-010)
// ============================================================================
export const MAX_BOTS_PER_LOBBY = 25; // Maximum bots per lobby (FR-001) - reduced to 1/4
export const BOT_POPULATION_CHECK_INTERVAL = 1000; // Check every 1 second
export const BOT_PERFORMANCE_BUDGET_MS = 2.0; // 2ms per bot per frame (NFR-002)
export const BOT_DECISION_INTERVAL = 1000; // Re-evaluate decisions every 1000ms (1 second) - less frequent but more decisive

// Bot behavior configuration (FR-005, FR-006)
export const BOT_BASE_ACCURACY = 0.85; // Good base accuracy with room to improve
export const BOT_ACCURACY_VARIANCE = 0.08; // ±8% variance for more natural spread
export const BOT_ACCURACY_SCALING = 0.003; // Meaningful accuracy improvement per pellet (FR-005)
export const BOT_REACTION_TIME_MIN = 80; // Minimum reaction time in ms (FR-005) - faster than most humans
export const BOT_REACTION_TIME_MAX = 180; // Maximum reaction time in ms - skilled human level
export const BOT_MISS_RATE = 0.0; // No intentional misses - they'll miss naturally

// Bot steering behavior constants (FR-006)
export const BOT_MAX_STEERING_FORCE = 650; // Maximum steering force - increased for more responsive movement
export const BOT_WANDER_RADIUS = 80; // Wander behavior circle radius - tighter for more focused movement
export const BOT_ARRIVAL_RADIUS = 80; // Slow down when within this distance of target - tighter control

// ============================================================================
// NAME GENERATION DATA
// ============================================================================
export const NAME_GEN_ADJECTIVES = [
  "Swift",
  "Bold",
  "Clever",
  "Silent",
  "Mighty",
  "Agile",
  "Fierce",
  "Noble",
  "Quick",
  "Brave",
  "Daring",
  "Sneaky",
  "Deadly",
  "Shadow",
  "Rapid",
  "Steel",
  "Thunder",
  "Blazing",
  "Cosmic",
  "Electric",
  "Neon",
  "Cyber",
  "Turbo",
  "Ultra",
  "Hyper",
  "Mega",
  "Super",
  "Alpha",
  "Prime",
  "Elite",
  "Royal",
  "Golden",
];

export const NAME_GEN_NOUNS = [
  "Wolf",
  "Eagle",
  "Tiger",
  "Falcon",
  "Dragon",
  "Phoenix",
  "Ninja",
  "Knight",
  "Warrior",
  "Hunter",
  "Ranger",
  "Sniper",
  "Ghost",
  "Phantom",
  "Storm",
  "Blade",
  "Viper",
  "Cobra",
  "Hawk",
  "Raven",
  "Panther",
  "Jaguar",
  "Fox",
  "Bear",
  "Shark",
  "Lion",
  "Cheetah",
  "Mantis",
  "Spider",
  "Scorpion",
  "Reaper",
  "Striker",
];

// ============================================================================
// MOBA MODE CONFIGURATION
// ============================================================================
export const MOBA_BASE_RADIUS = 80;
export const MOBA_BASE_MAX_HEALTH = 1000;
export const MOBA_NPC_RADIUS = 8;
export const MOBA_NPC_MAX_HEALTH = 30;
export const MOBA_NPC_SPEED = 100;
export const MOBA_NPC_DAMAGE = 5;
export const MOBA_NPC_ATTACK_RANGE = 50;
export const MOBA_NPC_ATTACK_COOLDOWN = 1000;
export const MOBA_NPC_SPAWN_INTERVAL = 30000;
export const MOBA_NPCS_PER_LANE = 3;
export const MOBA_NPC_XP_REWARD = 10;

// ============================================================================
// CONFIG REGISTRY - Register immutable configs
// ============================================================================

/**
 * Frozen world configuration
 * Demonstrates config registry usage - prevents accidental mutations
 */
export const WORLD_FROZEN = createConfig("world", {
  w: WORLD.w,
  h: WORLD.h,
});
